import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MDEditor, { ICommand, TextState, TextAreaTextApi } from '@uiw/react-md-editor';
import { tags as tagsList } from '@/pages/InterviewQuestion/data';
import { Select, message, Input, adUtils } from 'starry-sky-ui';
import { interviewManageAPI, getInterviewById } from '@/service/interview';
import '../Write/index.scss';

// 图片上传命令
const imageUploadCommand: (command: ICommand) => ICommand = (command) => {
  return {
    ...command,
    buttonProps: { 'aria-label': '上传图片' },
    execute: async (state: TextState, api: TextAreaTextApi) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';

      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;

        try {
          // 压缩图片：最长边 1920px，质量 0.8，降低上传带宽
          const compressed = await adUtils.compressImage(file, {
            quality: 0.8,
            maxDimension: 1920,
          });
          // 将 Blob 转回 File，保留原文件名
          const uploadFile = new File([compressed], file.name, { type: compressed.type });

          const formData = new FormData();
          formData.append('image', uploadFile);

          const res = await import('@/utils/request').then((m) =>
            m.default.post<{ url: string }>('/upload/image', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            })
          );

          if (!res.success || !res.data?.url) {
            throw new Error(res.message || '上传失败');
          }

          const selectedText = state.selectedText || 'image';
          const imageMarkdown = `![${selectedText}](${res.data.url})`;
          api.replaceSelection(imageMarkdown);
        } catch (error) {
          console.error('图片上传失败:', error);
          message.error('图片上传失败');
        }
      };

      input.click();
    },
  };
};

const Write: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get('postsId');

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tag, setTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);

  const isEdit = !!editId;

  // 编辑模式：获取文章数据
  useEffect(() => {
    if (!editId) return;

    setFetchLoading(true);
    getInterviewById(Number(editId))
      .then((res) => {
        if (res.success && res.data) {
          const article = res.data;
          setTitle(article.title || '');
          setContent(article.content || '');
          setTag(article.tag || '');
        } else {
          message.error('文章不存在或已被删除');
          navigate('/admin/interview-manage', { replace: true });
        }
      })
      .catch(() => {
        message.error('获取文章失败');
      })
      .finally(() => {
        setFetchLoading(false);
      });
  }, [editId, navigate]);

  // 标签回显：当 tagsList 加载完成后，用 article.tags 中的原始值匹配正确的 value
  useEffect(() => {
    if (!isEdit || !tag || tagsList.length === 0) return;
    // 尝试在 tagsList 中匹配：先用 value 匹配，再用 label 匹配
    const matched = tagsList.find(
      (t) => t.value === tag || t.label === tag
    );
    if (matched && matched.value !== tag) {
      setTag(String(matched.value));
    }
  }, [isEdit, tag, tagsList]);

  const handleSave = async (publishStatus: boolean) => {
    if (!title.trim()) {
      message.error('请输入文章标题');
      return;
    }
    if (!tag) {
      message.error('请选择标签');
      return;
    }
    if (!content.trim()) {
      message.error('请输入文章内容');
      return;
    }

    const params = {
      title: title.trim(),
      content,
      tag,
    };

    setLoading(true);
    try {
      let res;
      if (isEdit) {
        res = await interviewManageAPI.update(Number(editId), params);
      } else {
        res = await interviewManageAPI.create(params);
      }

      if (res?.success) {
        setTitle('');
        setContent('');
        setTag('');
        message.success(
          isEdit
            ? '文章已更新！'
            : '文章已发布！'
        );

        if (isEdit) {
          navigate('/admin/interview-manage');
        }
      }
    } catch {
      message.error(isEdit ? '更新文章失败' : '保存文章失败');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="write-container">
        <div className="write-loading">加载中...</div>
      </div>
    );
  }

  return (
    <div className="write-container">
      <div className="write-header">
        <h1 className="write-title">{isEdit ? '编辑文章' : '写文章'}</h1>
        {isEdit && (
          <span className="write-edit-hint">正在编辑文章 #{editId}</span>
        )}
      </div>

      <div className="write-form">
        <div className="form-section">
          <label className="form-label">
            标题 <span className="required">*</span>
          </label>
          <Input
            placeholder="请输入文章标题"
            value={title}
            onChange={(val) => setTitle(val)}
            size="large"
            maxLength={100}
            showCount
          />
        </div>

        <div className="form-section">
          <label className="form-label">
            标签 <span className="required">*</span>
          </label>
          <Select
            options={tagsList || []}
            value={tag}
            onChange={(val) => setTag(String(val || ''))}
            placeholder="请选择标签"
          />
        </div>



        <div className="form-section markdown-section">
          <label className="form-label">
            文章内容（支持 Markdown 格式）<span className="required">*</span>
          </label>
          <div className="md-editor-wrapper" data-color-mode="dark">
            <MDEditor
              value={content}
              onChange={(val) => setContent(val || '')}
              height={500}
              preview="edit"
              highlightEnable={false}
              commandsFilter={(command) => {
                if (command.name === 'help') {
                  return false;
                }
                if (command.name === 'image') {
                  return imageUploadCommand(command);
                }
                return command;
              }}
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            className="publish-btn"
            onClick={() => handleSave(true)}
            disabled={loading}
          >
            {loading ? '发布中...' : isEdit ? '更新并发布' : '发布文章'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Write;
